<script setup>
import { useForm } from "@/form";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import RssSourceModal from "../Modals/RssSourceModal.vue";
import NewsSourceModal from "../Modals/NewsSourceModal.vue";
import SitemapSourceModal from "../Modals/SitemapSourceModal.vue";
import UrlSourceModal from "../Modals/UrlSourceModal.vue";
import YoutubeSourceModal from "../Modals/YoutubeSourceModal.vue";
import KeywordsSourceModal from "../Modals/KeywordsSourceModal.vue";
import { FormInput, FormSwitch, FormTextarea } from "@Components/Form";
import { GoogleIcon, LoadingIcon, MagicIcon, YouTubeIcon } from "@Icons/index";
import { ChevronUpIcon, ExternalLinkIcon, FileTextIcon, InfoIcon, LinkIcon, NewspaperIcon } from "lucide-vue-next";
import Tooltip from "@Components/Tooltip.vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import DataLinksPart from "./DataLinksPart.vue";

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const { t: __ } = useI18n();

const customDataModal = ref();

const rewriteTitleForm = useForm({
    title: null,
    language: null,
});

const generateSeoTitleForm = useForm({
    title: null,
    language: null,
});

const generateSeoDescriptionForm = useForm({
    title: null,
    language: null,
});

const rssSourceModal = ref();
const newsSourceModal = ref();
const sitemapSourceModal = ref();
const urlSourceModal = ref();
const youtubeSourceModal = ref();
const keywordsSourceModal = ref();

const sources = [
    {
        id: "post",
        icon: FileTextIcon,
        name: "Mots clés",
        handle: () => {
            keywordsSourceModal.value.show();
        },
    },
    {
        id: "news",
        icon: NewspaperIcon,
        name: "Contenu d'actualité",
        handle: () => {
            newsSourceModal.value.show();
        },
    },
    {
        id: "url",
        icon: LinkIcon,
        name: "URL",
        handle: () => {
            urlSourceModal.value.show();
        },
    },
    {
        id: "youtube",
        icon: YouTubeIcon,
        name: "YouTube",
        handle: () => {
            youtubeSourceModal.value.show();
        },
    },
];

const rewriteTitle = async () => {
    if (props.modelValue.title?.length > 0) {
        rewriteTitleForm.title = props.modelValue.title;
        rewriteTitleForm.language = props.modelValue.language;

        const response = await rewriteTitleForm.post("single_mode_rewrite_title");

        props.modelValue.title = response.new_title ?? props.modelValue.title;
    }
};

const generateSeoTitle = async () => {
    if (props.modelValue.title?.length > 0) {
        generateSeoTitleForm.title = props.modelValue.title;
        generateSeoTitleForm.language = props.modelValue.language;

        const response = await generateSeoTitleForm.post("single_mode_generate_seo_title");

        props.modelValue.settings.seo_title = response.title ?? props.modelValue.settings.seo_title;
    }
};

const generateSeoDescription = async () => {
    if (props.modelValue.title?.length > 0) {
        generateSeoDescriptionForm.title = props.modelValue.title;
        generateSeoDescriptionForm.language = props.modelValue.language;

        const response = await generateSeoDescriptionForm.post("single_mode_generate_seo_description");

        props.modelValue.settings.seo_description = response.description ?? props.modelValue.settings.seo_description;
    }
};

const selectRssItem = (item) => {
    props.modelValue.type = "rss";
    props.modelValue.meta.url = item.url;
    props.modelValue.meta.real_title = item.title;
    props.modelValue.title = item.title;

    rewriteTitle();
};

const selectNewsItem = (item) => {
    props.modelValue.type = "news";
    props.modelValue.title = item.title;
    props.modelValue.meta.url = item.url;

    rewriteTitle();
};

const selectSitemapItem = (item) => {
    props.modelValue.type = "sitemap";
    props.modelValue.meta.url = item.url;
    props.modelValue.title = item.title;

    rewriteTitle();
};

const selectUrlItem = (item) => {
    props.modelValue.type = "url";
    props.modelValue.title = item.title;
    props.modelValue.meta.url = item.url;

    rewriteTitle();
};

const selectYoutubeItem = (item) => {
    props.modelValue.type = "youtube";
    props.modelValue.title = item.title;
    props.modelValue.meta.url = item.url;
    props.modelValue.meta.videoId = item.videoId;
    rewriteTitle();
};

const selectKeywordsItem = (item) => {
    props.modelValue.type = "post";
    props.modelValue.title = item.title;
    props.modelValue.meta.keywords = item.keywords;
};
</script>
<template>
    <Disclosure v-if="modelValue.id === undefined" as="div" v-slot="{ open }">
        <DisclosureButton
            :class="[
                open ? 'rounded-t-lg' : 'rounded-lg',
                'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
            ]">
            <span>{{ __("Source du contenu") }}</span>
            <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
        </DisclosureButton>
        <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
            <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-4 lg:grid-cols-2 sm:grid-cols-4">
                <div
                    @click="source.handle"
                    v-for="(source, index) in sources"
                    :key="index"
                    :class="[
                        'p-4 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800',
                        modelValue.type === source.id ? 'ring-2 ring-inset ring-gray-300 dark:ring-gray-700' : '',
                    ]">
                    <component :is="source.icon" class="w-6 h-6 text-gray-600 dark:text-gray-400 dark:group-hover:text-gray-200 group-hover:text-gray-700" />
                    <div class="mt-1.5 text-sm text-center text-gray-900 dark:text-white">{{ source.name }}</div>
                </div>
            </div>
        </DisclosurePanel>
    </Disclosure>
    <RssSourceModal ref="rssSourceModal" @submit="selectRssItem" />
    <NewsSourceModal ref="newsSourceModal" @submit="selectNewsItem" :language="modelValue.language" />
    <SitemapSourceModal ref="sitemapSourceModal" @submit="selectSitemapItem" />
    <UrlSourceModal ref="urlSourceModal" @submit="selectUrlItem" />
    <YoutubeSourceModal ref="youtubeSourceModal" @submit="selectYoutubeItem" />
    <KeywordsSourceModal ref="keywordsSourceModal" @submit="selectKeywordsItem" :language="modelValue.language" />
    <div>
        <FormInput
            v-model="modelValue.title"
            :label="__('Titre de l\'article')"
            :class="['rss', 'youtube', 'news', 'sitemap', 'url'].includes(modelValue.type) ? 'pr-[3.25rem]' : 'pr-8'">
            <template #action>
                <div class="absolute inset-y-0 right-0 flex items-center gap-x-1 mr-2 select-none">
                    <Tooltip v-if="modelValue.title.length > 0" :content="__('Idées de titres')" placement="top">
                        <MagicIcon
                            @click="$refs.keywordsSourceModal.show(modelValue.title)"
                            class="w-4 h-4 text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
                    </Tooltip>
                    <Tooltip v-if="['rss', 'youtube', 'news', 'sitemap', 'url'].includes(modelValue.type)" :content="__('Voir la source')" placement="top">
                        <a :href="modelValue.meta.url" target="_blank">
                            <ExternalLinkIcon class="w-4 h-4 text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
                        </a>
                    </Tooltip>
                </div>
            </template>
        </FormInput>
    </div>
    <Disclosure as="div" v-slot="{ open }" default-open>
        <DisclosureButton
            :class="[
                open ? 'rounded-t-lg' : 'rounded-lg',
                'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
            ]">
            <span>{{ __("Options SEO") }}</span>
            <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
        </DisclosureButton>
        <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
            <FormSwitch
                v-model="modelValue.settings.data.serp.enabled"
                :help="__('Récupérer les intentions de recherche sur votre requête depuis les résultats de Google')">
                <div class="flex items-center gap-2">
                    <GoogleIcon class="w-4 h-4" />
                    {{ __("Intentions de recherche") }}
                    <a @click.stop v-if="modelValue.title.length > 0" :href="`https://www.google.com/search?q=${modelValue.title}`" target="_blank">
                        <ExternalLinkIcon class="w-4 h-4 text-gray-600 dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
                    </a>
                </div>
            </FormSwitch>
            <FormInput v-model="modelValue.settings.seo_title" :label="__('Balise title SEO')" class="pr-10">
                <template #action>
                    <div class="absolute inset-y-0 right-0 flex items-center mr-3">
                        <Tooltip :content="__('Générer un titre SEO')" placement="top">
                            <button
                                @click.stop="generateSeoTitle"
                                type="button"
                                :class="[
                                    'flex items-center gap-1.5  cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                ]">
                                <LoadingIcon v-if="generateSeoTitleForm.processing" class="size-4" />
                                <MagicIcon v-else class="size-4" />
                            </button>
                        </Tooltip>
                    </div>
                </template>
            </FormInput>

            <FormTextarea
                v-model="modelValue.settings.seo_description"
                :label="__('Meta description SEO')"
                :rows="5"
                :left-actions="[
                    {
                        id: 'generate',
                        icon: MagicIcon,
                        processing: generateSeoDescriptionForm.processing,
                        disabled: modelValue.title.trim().length === 0,
                        label: __('Générer'),
                        onClick: generateSeoDescription,
                    },
                    {
                        id: 'magic-prompt',
                    },
                ]"
                :right-actions="[
                    {
                        id: 'maximize',
                    },
                ]">
            </FormTextarea>
        </DisclosurePanel>
    </Disclosure>
    <Disclosure as="div" v-slot="{ open }" default-open>
        <DisclosureButton
            :class="[
                open ? 'rounded-t-lg' : 'rounded-lg',
                'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
            ]">
            <span>{{ __("Personnaliser les données utilisées pour la génération") }}</span>
            <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
        </DisclosureButton>
        <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
            <div class="flex gap-2 text-sm">
                <InfoIcon class="w-4 h-4 flex-none text-gray-600 dark:text-gray-400" />
                <div class="flex-1 italic leading-4 text-gray-600 dark:text-gray-400">
                    {{
                        __(
                            "Si vous n'ajoutez pas d'URLs, elles s'ajouteront automatiquement avec les meilleurs résultats trouvés sur Google. (À la génération)."
                        )
                    }}
                </div>
            </div>
            <DataLinksPart v-model="modelValue.settings.data.links" />
        </DisclosurePanel>
    </Disclosure>
</template>
